import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useExpediente } from "@/context/ExpedienteContext";
import { Spinner } from "@/components/common";
import { ExpEstadoBadge } from "@/components/portalCommon";
import { UploadCloud, CheckCircle2, ClipboardCheck, Send, FileText, X } from "lucide-react";

export default function Expediente() {
  const { user } = useAuth();
  const { exp, meta, error, reload } = useExpediente();
  const navigate = useNavigate();
  const [showReview, setShowReview] = useState(false);
  const [sending, setSending] = useState(false);

  if (error) return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="exp-error">{error}</div>;
  if (!exp) return <div className="flex h-64 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;

  const dp = exp.hoja_vida?.datos_personales || {};
  const pendientes = exp.pendientes || [];
  const puedeEnviar = exp.estado === "borrador" && pendientes.length === 0;

  const enviar = async () => {
    setSending(true);
    try {
      await api.post("/portal/enviar");
      toast.success("Expediente enviado a HUMAN");
      setShowReview(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const Paso = ({ icon: Icon, titulo, desc, done }) => (
    <div className="flex items-start gap-3">
      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${done ? "bg-emerald-50 text-emerald-600" : "bg-vino-50 text-vino-700"}`}>
        {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
      </div>
      <div><p className="text-sm font-semibold text-slate-800">{titulo}</p><p className="text-sm text-slate-500">{desc}</p></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Hola, {user.name?.split(" ")[0] || "bienvenido"}</h1>
          <p className="mt-1 text-sm text-slate-500">Completa tu información y prepara tu ingreso.</p>
        </div>
        <ExpEstadoBadge estado={exp.estado} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold text-slate-900">Resumen</h2>
            <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2 xl:grid-cols-3">
              <div className="min-w-0" data-testid="resumen-nombre"><p className="text-xs text-slate-400">Nombre</p><p className="break-words font-medium leading-snug text-slate-800">{dp.nombres || exp.nombre} {dp.primer_apellido || ""}</p></div>
              <div className="min-w-0" data-testid="resumen-documento"><p className="text-xs text-slate-400">Documento</p><p className="break-words font-medium leading-snug text-slate-800">{dp.tipo_documento || exp.tipo_documento} {dp.numero_documento || exp.numero_documento}</p></div>
              <div className="min-w-0" data-testid="resumen-empresa"><p className="text-xs text-slate-400">Empresa cliente</p><p className="break-words font-medium leading-snug text-slate-800">{exp.empresa_cliente || "—"}</p></div>
              <div className="min-w-0" data-testid="resumen-documentos"><p className="text-xs text-slate-400">Documentos</p><p className="font-medium text-slate-800">{exp.documentos?.length || 0} cargados</p></div>
              <div className="min-w-0" data-testid="resumen-pendientes"><p className="text-xs text-slate-400">Pendientes</p><p className="font-medium text-slate-800">{pendientes.length}</p></div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 font-display text-lg font-semibold text-slate-900">¿Cómo empezar?</h2>
            <p className="mb-4 text-sm text-slate-500">Puedes empezar subiendo tus documentos o diligenciando tu información manualmente.</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate("/hoja-vida")} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-vino-800" data-testid="ir-hoja-vida"><ClipboardCheck className="h-4 w-4" /> Diligenciar hoja de vida</button>
              <button onClick={() => navigate("/documentos")} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-vino-700 hover:bg-vino-50" data-testid="ir-documentos"><UploadCloud className="h-4 w-4" /> Subir documentos</button>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-vino-100 bg-vino-50/50 p-5">
            <h2 className="mb-4 font-display text-base font-semibold text-vino-900">Tus próximos pasos</h2>
            <div className="space-y-4">
              <Paso icon={ClipboardCheck} titulo="Completa tu hoja de vida" desc="Diligencia tus datos por secciones." done={pendientes.length === 0} />
              <Paso icon={UploadCloud} titulo="Adjunta tus documentos" desc="Subir un archivo no significa que esté validado." done={(exp.documentos?.length || 0) > 0} />
              <Paso icon={Send} titulo="Envía a HUMAN" desc="HUMAN revisará tu expediente y te informará." done={exp.estado !== "borrador"} />
            </div>
          </div>

          {exp.estado === "borrador" && (
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-3 text-sm text-slate-600">Cuando termines, revisa tu información y envíala. <strong>Una vez enviada no podrás modificarla.</strong></p>
              <button disabled={!puedeEnviar} onClick={() => setShowReview(true)} data-testid="btn-abrir-revision"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-vino-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">
                <Send className="h-4 w-4" /> Revisar y enviar
              </button>
              {!puedeEnviar && <p className="mt-2 text-xs text-amber-600">Tienes {pendientes.length} pendiente(s). Revísalos antes de enviar.</p>}
            </div>
          )}
        </div>
      </div>

      {showReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowReview(false)} />
          <div className="relative z-10 w-full max-w-lg animate-fade-in-up rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-slate-900">Revisa la información</h3>
              <button onClick={() => setShowReview(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-4 text-sm text-slate-500">Confirma que tu información es correcta. Tú la revisas y confirmas antes de enviarla a HUMAN.</p>
            <div className="revision-filas space-y-2 rounded-xl border border-slate-200 p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Nombre</span><span className="font-medium text-slate-800">{dp.nombres} {dp.primer_apellido} {dp.segundo_apellido}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Documento</span><span className="font-medium text-slate-800">{dp.tipo_documento} {dp.numero_documento}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Celular</span><span className="font-medium text-slate-800">{dp.celular || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Correo</span><span className="font-medium text-slate-800">{dp.correo_electronico || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Documentos adjuntos</span><span className="font-medium text-slate-800">{exp.documentos?.length || 0}</span></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowReview(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Volver</button>
              <button onClick={enviar} disabled={sending} data-testid="btn-confirmar-enviar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
                {sending ? <Spinner /> : <Send className="h-4 w-4" />} Confirmar y enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
